import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Button from '../Button';
import SVG_ICONS from '~/assets/icons/icons';

const FORWARD_ENABLED_BUFFER = 60;

const BarContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: space-between;

  ${({ isScrollEnabled }) => (!isScrollEnabled ? 'padding-left: 10px' : '')}
`;

const BarContent = styled.div`
  flex-grow: 1;
  overflow-x: hidden;
`;

const Scrollable = styled.ul`
  position: relative;
  left: ${({ currentScroll }) => currentScroll}px;
  width: 100%;
  height: 100%;
  white-space: nowrap;
  list-style: none;
  transition: left 0.5s;
`;

const ButtonPlaceholder = styled.div`
  width: 36px;
  height: 100%;
  flex-shrink: 0;
`;

const ScrollableItem = styled.li`
  height: 100%;
  min-width: 87px;
  flex-basis: 100%;
  flex-grow: 1;
  display: inline-block;
`;

export const calculateScrollDiff = (container, elements) => {
  const actualWidth = Array.from(elements).reduce((res, elm) => res + elm.offsetWidth, 0);

  return actualWidth - container.offsetWidth;
};

export const calculateScrollToItem = (container, focusedElement, currentScroll) => {
  const containerWidth = container.offsetWidth,
    middle = containerWidth / 2,
    elementWidth = focusedElement.offsetWidth,
    elementLeft = focusedElement.offsetLeft;

  let scrollBy = 0;

  if (elementLeft + elementWidth - currentScroll > containerWidth) {
    scrollBy = elementLeft - middle + (elementLeft + elementWidth - containerWidth);
  } else if (elementLeft > middle) {
    scrollBy = elementLeft - middle;
  }

  return Math.ceil(scrollBy);
};

export const calculateScrollBack = (container, scrollStep, currentScroll) => {
  const jump = container.offsetWidth * (scrollStep / 100);

  return Math.min(jump, currentScroll) * -1;
};

export const calculateScrollForward = (container, scrollStep, scrollDiff, currentScroll) => {
  const jump = container.offsetWidth * (scrollStep / 100),
    remaining = scrollDiff + FORWARD_ENABLED_BUFFER - currentScroll,
    increase = Math.min(jump, remaining);

  //if increase takes scroll close to end (less than buffer) then scroll all the way to the end
  return remaining - increase < FORWARD_ENABLED_BUFFER ? remaining : increase;
};

export const getIsForwardEnabled = (isScrollEnabled, currentScroll, scrollDiff) => isScrollEnabled && currentScroll < scrollDiff + FORWARD_ENABLED_BUFFER;

const ScrollableBar = ({
  items,
  itemClass = 'scrollable-item',
  ItemComponent,
  onItemClick,
  focusedItemIndex,
  itemProps,
  //percent of container width to jump when back/forward button is clicked
  scrollStep = 30,
}) => {
  const [scrollDiff, setScrollDiff] = useState(0);
  const [currentScroll, setCurrentScroll] = useState(0);
  const containerRef = useRef(null);

  const isScrollEnabled = scrollDiff > 0,
    isForwardEnabled = getIsForwardEnabled(containerRef.current, currentScroll, scrollDiff);

  const increaseScrollBy = useCallback((x) => {
    //using function setter so this callback isnt dependant on currentScroll value
    setCurrentScroll((currentScroll) => currentScroll + Math.floor(x));
  }, []);

  const scrollBack = useCallback(() => {
    increaseScrollBy(calculateScrollBack(containerRef.current, scrollStep, currentScroll));
  }, [increaseScrollBy, currentScroll, scrollStep]);

  const scrollForward = useCallback(() => {
    increaseScrollBy(calculateScrollForward(containerRef.current, scrollStep, scrollDiff, currentScroll));
  }, [increaseScrollBy, currentScroll, scrollDiff, scrollStep]);

  useEffect(() => {
    const container = containerRef.current;
    const elements = container.querySelectorAll(`.${itemClass}`);

    //calculate the diff between space taken by items compared to the container = whether scroll is needed or not
    const newScrollDiff = calculateScrollDiff(container, elements);

    if (scrollDiff !== newScrollDiff) {
      setScrollDiff(newScrollDiff);
    }
  }, [items, itemClass, scrollDiff]);

  useEffect(() => {
    if (isScrollEnabled && ~focusedItemIndex) {
      //calculate whether scroll change is needed to show the current focused item
      const container = containerRef.current;
      const focusedElement = container.querySelector(`.${itemClass}:nth-child(${focusedItemIndex + 1})`);

      setCurrentScroll((currentScroll) => calculateScrollToItem(container, focusedElement, currentScroll));
    }
  }, [isScrollEnabled, focusedItemIndex, itemClass]);

  return (
    <BarContainer ref={containerRef} isScrollEnabled={isScrollEnabled} data-test="scrollabale-bar">
      {isScrollEnabled &&
        (currentScroll > 0 ? (
          <Button
            icon={SVG_ICONS.KEYBOARD_ARROW_LEFT}
            iconSize="xs"
            onClick={scrollBack}
            tabIndex={0}
            className="c-inactive-tab-icon fill-inactive-tab-icon"
          />
        ) : (
          <ButtonPlaceholder />
        ))}

      <BarContent>
        <Scrollable currentScroll={currentScroll * -1}>
          {items.map((i) => (
            <ScrollableItem key={i.id} data-id={i.id} className={itemClass}>
              <ItemComponent {...i} {...itemProps} tabIndex={0} onClick={onItemClick} />
            </ScrollableItem>
          ))}
        </Scrollable>
      </BarContent>

      {isScrollEnabled &&
        (isForwardEnabled ? (
          <Button
            icon={SVG_ICONS.KEYBOARD_ARROW_RIGHT}
            iconSize="xs"
            onClick={scrollForward}
            tabIndex={0}
            className="c-inactive-tab-icon fill-inactive-tab-icon"
          />
        ) : (
          <ButtonPlaceholder />
        ))}
    </BarContainer>
  );
};

export default ScrollableBar;
